import React, { useCallback, useEffect, useState } from 'react';

import { useFormikContext } from 'formik';
import {
  find as _find, first as _first, isUndefined as _isUndefined,
  toNumber as _toNumber,
} from 'lodash';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';

import { Box, Checkbox, FormControlLabel, InputLabel } from '@material-ui/core';

import { useUI } from '../../../app/context/ui';
import { WizardStyles } from '../../../assets/css';
import GoogleAutocomplete from '../../../components/shared/GoogleAutocomplete';
import GoogleMap from '../../../components/shared/GoogleMap';
import PropertyImage from '../../../components/shared/PropertyImage';
import {
  Container, Grid, Typography,
} from '../../../components/shared/MaterialUI';
import {
  CheckboxField, InputField, NumberFormatCustom, SelectField,
} from '../../../forms';
import { Utils } from '../../../helpers';
import AppHelper from '../../../helpers/AppHelper';
import store from '../../../redux/store';
import {
  LawnAnalyzerService,
  MarketService,
  MelissaService,
  StateService,
  UserService
} from '../../../services';

const marketService = new MarketService();
const melissaService = new MelissaService();
const stateService = new StateService();
const userService = new UserService();
const lawnAnalyzerService = new LawnAnalyzerService();

const Step2 = (props) => {
  const [markets, setMarkets] = useState([]);
  const [citys, setCitys] = useState([]);
  const [states, setStates] = useState([]);
  const [csrs, setCsrs] = useState([]);

  const [location, setLocation] = useState({});
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [melissaData, setMelissaData] = useState(null);

  const { blockUI, snackbarUI } = useUI();

  // Google Maps
  const [mapLocation, setMapLocation] = useState({
    lat: 37.09024,
    lng: -95.712891,
  });

  const [mapZoom, setMapZoom] = useState(2);
  const [mapText, setMapText] = useState('');

  const wizardStyle = WizardStyles();
  const {
    formField: {
      city,
      property,
      state,
      serviceAddress,
      owner,
      csr,
      market,
      lotSize,
      taxRate,
      useCornerLot,
    },
  } = props;
  const { formikRef } = props;
  const { values: formValues } = useFormikContext();
  const {
    zipcode,
    marketId,
    costService,
    isServiceAddressManual,
  } = formValues;
  const stat = store.getState();
  const accessToken = stat.user?.accessToken;

  const isTexas = () => {
    const stateObj = _find(states, { value: formValues.state });
    if (!stateObj) return false;
    const label = (stateObj.label || '').trim().toLowerCase();
    return label === 'texas' || label === 'tx';
  };

  const getListMarket = useCallback(async () => {
    try {
      blockUI.current.open(true);
      const r1 = await marketService.read(marketId);
      let arr = [];
      const obj = Utils.updateObjectKey(r1.data);

      if (formikRef.current) {
        formikRef.current.setFieldValue('taxRate', obj.taxRate);
        formikRef.current.setFieldValue('useCornerLot', "");
        formikRef.current.setFieldValue('market', obj.value);
        formikRef.current.setFieldValue('marketName', obj.label);
      }
      arr.push(obj);
      setMarkets(arr);
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  }, [blockUI, marketId, formikRef, snackbarUI]);

  const getZipCodeState = async () => {
    try {
      const r1 = await stateService.zip(zipcode);
      const arrCity = Utils.updateArrayObjectItem(r1.data, [], 'id', 'city');
      const arrState = Utils.updateArrayObjectItem(r1.data, [], 'stateId', 'stateName');

      const objCity = _first(arrCity);
      const objState = _first(arrState);

      if (formikRef.current) {
        formikRef.current.setFieldValue('city', objCity.value);
        formikRef.current.setFieldValue('cityName', objCity.label);
        formikRef.current.setFieldValue('state', objState.value);
      }

      setLocation({
        lat: _toNumber(objCity.lat),
        lng: _toNumber(objCity.lng),
      });

      setCitys(arrCity);
      setStates(arrState);
    } catch (e) {
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const getListUser = async () => {
    try {
      const r1 = await userService.list('?filter[role]=customer-service&filter[status]=1');
      const arrCsr = Utils.updateArrayObjectItem(
        r1.data,
        [{ value: null, label: '-- Select CSR --' }],
        'id',
        'fullName'
      );
      setCsrs(arrCsr);
      if (formikRef.current) {
        if (_toNumber(costService) === 0) {
          formikRef.current.setFieldValue('csr', props.user.id);
        }
      }
    } catch (e) {
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const onKeyPress = () => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('reasonEdit', false);
    }
  };

  useEffect(() => {
    marketService.init(accessToken);
    getListMarket();
  }, [accessToken]);

  useEffect(() => {
    stateService.init(accessToken);
    getZipCodeState();
  }, [accessToken]);

  useEffect(() => {
    userService.init(accessToken);
    getListUser();
  }, [accessToken]);

  useEffect(() => {
    if (formikRef.current) {
      let cityOld = _find(citys, { value: formValues.city });
      cityOld = !_isUndefined(cityOld) ? cityOld : [];
      let address = '';
      if (!_isUndefined(formValues.serviceAddress)) {
        if (formValues.serviceAddress.length > 0) {
          address =
            formValues.serviceAddress +
            ', ' +
            cityOld.label +
            ', ' +
            formValues.state +
            ' ' +
            zipcode;
        }
      }
      setLocation({
        lat: location.lat,
        lng: location.lng,
        address: address,
      });
    }
  }, [formValues.serviceAddress, formValues.state, formValues.city]);

  const buildFullAddress = () => {
    if (isServiceAddressManual) {
      const cityObj = _find(citys, { value: formValues.city });
      const cityName = cityObj ? cityObj.label : '';
      return `${formValues.serviceAddress}, ${cityName}, ${formValues.state} ${zipcode}`;
    }
    return formValues.serviceAddress.replace(/, USA$/, '') + ' ' + zipcode;
  };

  const getMelissaData = async () => {
    try {
      blockUI.current.open(true);
      setAnalysisLoading(true);
      setAnalysisData(null);
      melissaService.getAccessToken();
      const response = await melissaService.getDataMowMellisa({
        address: formValues.serviceAddress,
        zip: zipcode,
      });
      const data = response.data;
      setMelissaData(data);

      if (formikRef.current) {
        formikRef.current.setFieldValue(lotSize.name, data?.size ?? '');
        formikRef.current.setFieldValue(owner.name, data?.owner ?? '');
        formikRef.current.setFieldValue(property.name, data?.pvalue ?? '');

        const isCornerLot = Boolean(formValues.useCornerLot);
        const effectiveSize = isCornerLot ? Math.round(data.size * 1.1) : data.size;
        let turfSize = '';
        if (data?.buildingArea > 0) {
          turfSize = Math.max(effectiveSize - data.buildingArea - 400, 0);
        }
        formikRef.current.setFieldValue('turfSize', turfSize);
      }

      blockUI.current.open(false);
      setAnalysisLoading(false);
    } catch (error) {
      blockUI.current.open(false);
      setAnalysisLoading(false);
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const getLawnAnalysis = async () => {
    try {
      const fullAddress = buildFullAddress();

      blockUI.current.open(true);
      setAnalysisLoading(true);
      const response = await lawnAnalyzerService.propertyAnalysis(fullAddress, zipcode);
      const data = response.data;

      setAnalysisData(data);

      const melissa = data.melissa;
      const analysis = data.analysis;

      if (formikRef.current) {
        formikRef.current.setFieldValue(owner.name, melissa?.owner || '');
        formikRef.current.setFieldValue(property.name, melissa?.pvalue || '');
        formikRef.current.setFieldValue(lotSize.name, melissa?.size ?? '');

        const isCornerLot = Boolean(formValues.useCornerLot);

        // Calculate Melissa-based turf size as a baseline
        const effectiveSize = isCornerLot ? Math.round(melissa.size * 1.1) : melissa.size;
        let melissaTurf = 0;
        if (melissa?.buildingArea > 0) {
          melissaTurf = Math.max(effectiveSize - melissa.buildingArea - 400, 0);
        }

        // Use property analysis turf size if available and larger than Melissa baseline
        let turfSize = melissaTurf;
        if (analysis?.lawnSqft > 0 && analysis.lawnSqft >= melissaTurf) {
          turfSize = isCornerLot ? Math.round(analysis.lawnSqft * 1.1) : analysis.lawnSqft;
        }

        formikRef.current.setFieldValue('turfSize', turfSize || '');
      }

      blockUI.current.open(false);
      setAnalysisLoading(false);
    } catch (error) {
      blockUI.current.open(false);
      setAnalysisLoading(false);
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const isWithinWorkHours = () => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    // Monday (1) through Friday (5), 6:00 AM to 6:59 PM
    return day >= 1 && day <= 5 && hour >= 6 && hour < 19;
  };

  const handleAddressAnalysis = () => {
    if (!formValues.serviceAddress) return;
    if (isTexas() && isWithinWorkHours()) {
      getLawnAnalysis();
    } else {
      getMelissaData();
    }
  };

  const handleChangeServiceAddressManualEnabled = (e) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        "isServiceAddressManual",
        e.target.checked
      );
    }
  };

  useEffect(() => {
    if (formValues.serviceAddress && !formValues.isServiceAddressManual) {
      handleAddressAnalysis();
    }
  }, [formValues.serviceAddress]);

  useEffect(() => {
    if (!formikRef.current) return;
    const isCornerLot = Boolean(formValues.useCornerLot);

    // Recalculate from lawn analysis data (Texas, work hours)
    if (analysisData?.melissa) {
      const melissa = analysisData.melissa;
      const analysis = analysisData.analysis;
      const effectiveSize = isCornerLot ? Math.round(melissa.size * 1.1) : melissa.size;
      let melissaTurf = 0;
      if (melissa?.buildingArea > 0) {
        melissaTurf = Math.max(effectiveSize - melissa.buildingArea - 400, 0);
      }
      let turfSize = melissaTurf;
      if (analysis?.lawnSqft > 0 && analysis.lawnSqft >= melissaTurf) {
        turfSize = isCornerLot ? Math.round(analysis.lawnSqft * 1.1) : analysis.lawnSqft;
      }
      formikRef.current.setFieldValue('turfSize', turfSize || '');
    }
    // Recalculate from Melissa-only data (non-Texas / outside work hours)
    else if (melissaData) {
      const effectiveSize = isCornerLot ? Math.round(melissaData.size * 1.1) : melissaData.size;
      let turfSize = '';
      if (melissaData?.buildingArea > 0) {
        turfSize = Math.max(effectiveSize - melissaData.buildingArea - 400, 0);
      }
      formikRef.current.setFieldValue('turfSize', turfSize);
    }
  }, [formValues.useCornerLot]);

  return (
    <Container component='main' maxWidth='lg' className={wizardStyle.ctnInner}>
      <Typography className={wizardStyle.title}>Street address</Typography>
      <hr />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>City *</InputLabel>
          <SelectField name={city.name} data={citys} autoFocus fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>Property value</InputLabel>
          <InputField
            name={property.name}
            inputProps={{ minlength: 1, maxLength: 15 }}
            InputProps={{
              inputComponent: NumberFormatCustom,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>State *</InputLabel>
          <SelectField name={state.name} data={states} autoFocus fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>

          <div className={wizardStyle.labelIcon}>
            <InputLabel className={wizardStyle.label}>Service address (US only) *</InputLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isServiceAddressManual}
                  onChange={handleChangeServiceAddressManualEnabled}
                  name="isServiceAddressManual"
                />
              }
              label="Manual"
              className={wizardStyle.controlLabel}
            />
          </div>

          {
            (isServiceAddressManual)
              ?
                <InputField
                  name={serviceAddress.name}
                  fullWidth
                  onBlur={() => handleAddressAnalysis()}
                />
              :
                <GoogleAutocomplete
                  serviceAddress={serviceAddress}
                  name={serviceAddress.name}
                  formikRef={formikRef}
                  setMapText={setMapText}
                  setMapLocation={setMapLocation}
                  setMapZoom={setMapZoom}
                />
          }

        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>Owner</InputLabel>
          <InputField name={owner.name} fullWidth />
        </Grid>
        <Grid item xs sm={4}>
          <InputLabel className={wizardStyle.label}>CSR *</InputLabel>
          <SelectField name={csr.name} data={csrs} autoFocus fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>Market *</InputLabel>
          <SelectField name={market.name} data={markets} autoFocus fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel className={wizardStyle.label}>Lot size</InputLabel>
          <InputField
            name={lotSize.name}
            onKeyUp={onKeyPress}
            InputProps={{ inputComponent: NumberFormat }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <InputLabel className={wizardStyle.label}>Tax rate *</InputLabel>
          <InputField
            name={taxRate.name}
            InputProps={{ inputComponent: NumberFormat }}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <CheckboxField name={useCornerLot.name} label={useCornerLot.label} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box my={2} />
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={analysisData ? 6 : 12}>
          <div style={{ height: '50vh', width: '100%' }}>
            <GoogleMap center={mapLocation} zoom={mapZoom} text={mapText} />
          </div>
        </Grid>
        {analysisData && analysisData.image && (
          <Grid item xs={6}>
            <PropertyImage
              imageData={analysisData.image}
              imageSize={analysisData.image_size}
              polygons={analysisData.polygons}
              height="50vh"
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};
export default connect(mapStateToProps)(Step2);
